import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/useAuth';
import { CheckIcon, SpinnerIcon } from './icons';
import type { OnboardingStatus } from './types';

/**
 * Step 0 — connect the Stripe account (Stripe App install flow, M30).
 *
 * UX improvement over the embedded wizard: instead of "open in a new tab +
 * click I've connected", we do a same-tab server-side redirect. The button
 * fetches the Stripe OAuth URL from the Worker (which scopes it to the
 * session's tenant), then we navigate the current tab there. Stripe redirects
 * back to the Worker callback, which exchanges the code server-side, then
 * redirects back to Pages /onboarding — and on return the status fetch shows
 * stripeConnected=true, auto-advancing.
 */
export function StepConnect({
  status,
  onConnected,
  stripeFirst = false,
}: {
  status: OnboardingStatus | null;
  onConnected: () => void;
  /** True for Stripe-first arrivals (came via the marketplace install). */
  stripeFirst?: boolean;
}) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Test-mode fork (M31 safety): choosing test mode first shows a confirmation
  // screen — test Stripe data still writes REAL records into the HubSpot
  // portal you're in, so evaluators on a production portal should switch to a
  // HubSpot developer test account first. Continue = acknowledged escape
  // hatch (fine for reviewers + sandbox portals).
  const [testFork, setTestFork] = useState(false);
  // Disconnect flow (2026-09-18): the escape hatch from a wrong/revoked
  // account connection. Admin-only (server enforces too); resets synced data
  // + the connection and returns this step to the connect card.
  const { principal } = useAuth();
  const isAdmin = principal?.role === 'admin';
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const handleDisconnect = async () => {
    setDisconnectBusy(true);
    setDisconnectError(null);
    try {
      await api.post('onboarding/stripe-app/disconnect', {
        deleteHubSpotRecords: true,
      });
      // Hard reload — the wizard re-derives everything from
      // /onboarding/status (now disconnected → this connect card).
      window.location.reload();
    } catch (e) {
      setDisconnectError(
        e instanceof ApiError ? e.message : 'Failed to disconnect — try again.'
      );
      setDisconnectBusy(false);
    }
  };

  // M31: 'test' connects via the TEST-channel install (fake money — for
  // reviewers, evaluators, and internal QA). 'live' is the default path.
  const handleConnect = async (mode: 'live' | 'test' = 'live') => {
    setConnecting(true);
    setError(null);
    try {
      const body = await api.get<{ url?: string; connected?: boolean; }>(
        mode === 'test' ? 'onboarding/stripe-app?mode=test' : 'onboarding/stripe-app'
      );
      if (body.connected) {
        // Already linked — advance straight to product mapping.
        onConnected();
        return;
      }
      if (body.url) {
        // Same-tab redirect — Stripe will redirect back to the Worker, which
        // redirects back to Pages /onboarding. No new tab, no manual refresh.
        // NOTE: do NOT reset `connecting` in the finally block — the button
        // must stay disabled until the browser actually navigates away.
        // Previously the finally reset it, so the button flashed back to
        // enabled during the redirect window. (QA #1)
        window.location.href = body.url;
        return;
      }
      setError('Unexpected response from the sync service.');
      setConnecting(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Network error reaching the sync service.');
      setConnecting(false);
    }
  };

  const stripeFirstAutoKey = 'ampdeck_stripe_first_auto';

  // One-shot auto-continue for Stripe-first arrivals: they already installed
  // the app from Stripe's marketplace (that's WHY they're here), so finish the
  // link automatically instead of making them click another "Connect Stripe"
  // button. sessionStorage guard = exactly one attempt per wizard session; a
  // failed attempt falls back to the manual button (no redirect loop).
  useEffect(() => {
    if (!stripeFirst || connecting) return;
    if (sessionStorage.getItem(stripeFirstAutoKey)) return;
    sessionStorage.setItem(stripeFirstAutoKey, '1');
    void handleConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeFirst]);

  if (status?.stripeConnected) {
    return (
      <div className="card p-6">
        <div className="flex items-center gap-3">
          <span className="badge-success">
            <CheckIcon className="h-3 w-3" /> connected
          </span>
          <span className="font-mono text-sm text-ink-600">{status.stripeAccountId}</span>
          {status.testMode && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              test mode
            </span>
          )}
        </div>
        <p className="mt-3 text-sm text-ink-500">
          Your Stripe account is connected{status.testMode ? ' in test mode — records synced from Stripe test data' : ''}. You can proceed to map your products.
        </p>
        <div className="mt-4 flex items-center gap-4">
          <button className="btn-primary" onClick={onConnected}>
            Continue
          </button>
          {isAdmin && (
            <button
              type="button"
              className="text-sm font-medium text-ink-400 transition-colors hover:text-warn-600"
              onClick={() => setDisconnectOpen(true)}
            >
              Use a different account
            </button>
          )}
        </div>

        {disconnectOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="disconnect-title"
            onClick={(e) => {
              if (e.target === e.currentTarget && !disconnectBusy) setDisconnectOpen(false);
            }}
          >
            <div className="card w-full max-w-md p-6">
              <h2 id="disconnect-title" className="text-lg font-semibold text-ink-900">
                Use a different Stripe account?
              </h2>
              <p className="mt-2 text-sm text-ink-600">
                This disconnects <span className="font-mono">{status.stripeAccountId}</span>{' '}
                and clears the data synced from it, then returns you to the
                connect step. Deals created in HubSpot by the sync are
                archived (recoverable for 90 days). Your team roles and
                settings are kept.
              </p>
              {disconnectError && (
                <p className="mt-3 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-600">
                  {disconnectError}
                </p>
              )}
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDisconnectOpen(false)}
                  disabled={disconnectBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDisconnect}
                  disabled={disconnectBusy}
                >
                  {disconnectBusy ? (
                    <>
                      <SpinnerIcon className="h-4 w-4" /> Disconnecting…
                    </>
                  ) : (
                    'Disconnect account'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Test-mode fork (M31 safety): shown after "Connect in test mode". The
  // Stripe side is test (fake money) but the HubSpot side is whatever portal
  // installed the app — make that consequence explicit BEFORE the first
  // record is written, then let the user choose.
  if (testFork) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-ink-900">Test mode writes to this HubSpot portal</h2>
        <p className="mt-2 text-sm text-ink-600">
          Test mode connects a Stripe account with <strong>test data</strong>{' '}
          (fake customers, subscriptions, invoices — no real charges). But the
          records AmpDeck creates — contacts, deals, pipelines — are written to{' '}
          <strong>this HubSpot portal</strong>, and a test customer whose email
          matches a real contact will update that contact's billing fields with
          test values.
        </p>
        <div className="mt-4 rounded-lg bg-brand-500/10 p-4 text-sm text-brand-800">
          <p className="font-medium">Evaluating on your production portal?</p>
          <p className="mt-1">
            The clean setup is a free HubSpot developer test account (a sandbox
            portal) paired with Stripe test mode. Create one in ~2 minutes,
            install AmpDeck there, and keep your production CRM untouched.
          </p>
          <a
            href="https://developers.hubspot.com/docs/developer-tooling/local-development/configurable-test-accounts"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block font-medium underline"
          >
            How to create a HubSpot test account →
          </a>
        </div>
        <div className="mt-5 flex flex-col items-start gap-2">
          <button
            className="btn btn-primary"
            onClick={() => handleConnect('test')}
            disabled={connecting}
          >
            {connecting ? (
              <>
                <SpinnerIcon className="h-4 w-4" /> Redirecting…
              </>
            ) : (
              'Continue — this is a test/sandbox portal'
            )}
          </button>
          <button
            className="text-sm font-medium text-ink-500 transition-colors hover:text-ink-800"
            onClick={() => setTestFork(false)}
          >
            ← Back
          </button>
          {error && (
            <p className="mt-1 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-600">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-ink-900">Connect your Stripe account</h2>
      {stripeFirst && (
        <p className="mt-2 rounded-lg bg-brand-500/10 px-3 py-2 text-sm text-brand-700">
          You already installed the app on Stripe — finishing the connection now
          (this should take you back to Stripe for a moment and return here
          automatically)…
        </p>
      )}
      <p className="mt-2 text-sm text-ink-600">
        Authorize AmpDeck to read your subscriptions, invoices, and customers —
        and to run the actions you trigger in HubSpot (refunds, subscription
        changes, payment links).
      </p>

      <div className="mt-5 flex flex-col gap-3 rounded-lg bg-ink-50 p-4 text-sm text-ink-600">
        <div className="flex items-start gap-2">
          <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-success-600" />
          <span>Connects to <strong>your</strong> existing Stripe account (not a new one).</span>
        </div>
        <div className="flex items-start gap-2">
          <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-success-600" />
          <span>You'll authorize on stripe.com, then return here automatically.</span>
        </div>
        <div className="flex items-start gap-2">
          <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-success-600" />
          <span>No card details or payout settings are changed.</span>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-600">
          {error}
        </p>
      )}

      <div className="mt-5 flex flex-col items-start gap-2">
        <button
          className="btn btn-primary"
          onClick={() => handleConnect('live')}
          disabled={connecting}
        >
          {connecting ? (
            <>
              <SpinnerIcon className="h-4 w-4" /> Redirecting…
            </>
          ) : (
            'Connect Stripe'
          )}
        </button>

        {/* M31: test-mode connect — reviewers + QA exercise the full sync on
            fake money (no real charges/refunds). Secondary on purpose:
            paying customers take the primary path. */}
        <div className="flex w-full items-center gap-3 text-xs text-ink-400" aria-hidden="true">
          <span className="h-px flex-1 bg-ink-200" />
          <span>or</span>
          <span className="h-px flex-1 bg-ink-200" />
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => setTestFork(true)}
          disabled={connecting}
        >
          Connect in test mode
        </button>
        <p className="mt-1 max-w-sm text-xs text-ink-500">
          For evaluations and safe testing — syncs your Stripe test data, no
          real charges. Switch to live mode any time by reconnecting.
        </p>
      </div>
    </div>
  );
}
